import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { getPlatformCostForTender } from '../../services/billingService';
import type { Tender } from '../../types/tender';
import { BaseModal } from '../common/UI/BaseModal';

export interface ArchiveTenderParams {
  reason: string;
  category: string;
  tenderValue?: number | null | undefined;
  bidCost?: number | null | undefined;
  includeInMetrics: boolean;
}

interface ArchiveTenderDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (params: ArchiveTenderParams) => void;
  isLoading?: boolean;
  tenderTitle?: string | null | undefined;
  tenderId?: string | null | undefined;
  initialValue?: number | null | undefined;
  initialCost?: number | null | undefined;
  initialInclude?: boolean | null | undefined;
}

const CATEGORIES = [
  'Won',
  'Lost',
  'Not Relevant',
  'Already Actioned',
  "Didn't respond in time",
  'Duplicate',
  'Not enough capacity',
  'Not cost effective',
  "Couldn't find appropriate partner",
  'Not aligned to capability',
  'Not aligned to strategic position / direction',
  'Unacceptable contract terms',
  'Other',
];

const ArchiveTenderDialog: React.FC<ArchiveTenderDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading = false,
  tenderTitle,
  tenderId,
  initialValue,
  initialCost,
  initialInclude,
}) => {
  const theme = useTheme();
  const { account } = useAccount();

  const [category, setCategory] = useState<string>('');
  const [reasonDetails, setReasonDetails] = useState('');
  const [tenderValue, setTenderValue] = useState<string>('');
  const [bidCost, setBidCost] = useState<string>('');
  const [includeInMetrics, setIncludeInMetrics] = useState<boolean>(true);
  const [platformCost, setPlatformCost] = useState(0);

  // Fetch platform cost when opening
  useEffect(() => {
    if (tenderId && account?.clientId && open) {
      const fetchCost = async () => {
        try {
          const dummyTender = { id: tenderId } as Tender; // Partial tender object for the service
          const cost = await getPlatformCostForTender(account.clientId, dummyTender);
          setPlatformCost(cost);
        } catch (_error) {
          // Ignore
        }
      };
      fetchCost();
    } else {
      setPlatformCost(0);
    }
  }, [tenderId, account?.clientId, open]);

  // Pre-populate if editing/opening
  useEffect(() => {
    if (open) {
      setCategory('');
      setReasonDetails('');
      setTenderValue(
        initialValue !== undefined && initialValue !== null ? initialValue.toString() : ''
      );
      setBidCost(initialCost !== undefined && initialCost !== null ? initialCost.toString() : '');
      setIncludeInMetrics(initialInclude !== false); // default to true unless explicitly false
    }
  }, [open, initialValue, initialCost, initialInclude]);

  const handleConfirm = () => {
    onConfirm({
      category,
      reason: category ? `${category} - ${reasonDetails.trim()}` : reasonDetails.trim(),
      tenderValue: tenderValue ? Number(tenderValue) : undefined,
      bidCost: bidCost ? Number(bidCost) : undefined,
      includeInMetrics,
    });
  };

  const isFormValid = !!category && !!reasonDetails.trim();

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      aria-labelledby="archive-dialog-title"
      maxWidth="sm"
      fullWidth
      title={`Archive Tender${tenderTitle ? `: ${tenderTitle}` : ''}`}
      actions={
        <>
          <Button onClick={onClose} color="inherit" disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            disabled={!isFormValid || isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : undefined}
            sx={{
              backgroundColor: theme.palette.bidlogiq?.brick || theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            {isLoading ? 'Archiving...' : 'Archive'}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        <Alert severity="warning">
          Note: You will no longer be able to access the documents generated for this tender. Please
          ensure you have saved everything you want to retain.
        </Alert>

        <FormControl fullWidth size="small">
          <InputLabel id="archive-category-label">Category</InputLabel>
          <Select
            labelId="archive-category-label"
            value={category}
            label="Category"
            onChange={(e) => setCategory(e.target.value)}
          >
            {CATEGORIES.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Reason Details"
          multiline
          minRows={3}
          maxRows={6}
          value={reasonDetails}
          onChange={(e) => setReasonDetails(e.target.value)}
          placeholder={
            category === 'Won'
              ? 'Why did we win?'
              : category === 'Lost'
                ? 'Why did we lose?'
                : 'Details...'
          }
          fullWidth
          size="small"
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, minHeight: 40 }}>
              The actual value of the project
            </Typography>
            <TextField
              label="Tender Value"
              type="number"
              value={tenderValue}
              onChange={(e) => setTenderValue(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Box>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, minHeight: 40 }}>
              Time and resources outside of the BIDLOGIQ platform
            </Typography>
            <TextField
              label="Bid Cost"
              type="number"
              value={bidCost}
              onChange={(e) => setBidCost(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                bgcolor: 'background.default',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Bid Cost
                </Typography>
                <Typography variant="body2">
                  $
                  {Number(bidCost || 0).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  + Platform Cost
                </Typography>
                <Typography variant="body2">
                  $
                  {platformCost.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  pt: 1,
                }}
              >
                <Typography variant="body1" fontWeight="bold">
                  Total Cost
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  $
                  {(Number(bidCost || 0) + platformCost).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={includeInMetrics}
              onChange={(e) => setIncludeInMetrics(e.target.checked)}
              color="primary"
            />
          }
          label="Include in metrics and insights"
        />
      </Box>
    </BaseModal>
  );
};

export default ArchiveTenderDialog;
